/**
 * =============================================================================
 * FALCON - Module Deneigement des Parkings Avion
 * =============================================================================
 *
 * @file        components/mobile/MobileNavBar.tsx
 * @description Barre de navigation mobile fixe en bas de l'ecran
 *
 * @version     2.4
 * @author      Equipe FALCON
 * =============================================================================
 */

import { useLocation, useNavigate } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  HomeIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import {
  HomeIcon as HomeIconSolid,
  UserCircleIcon as UserIconSolid,
} from '@heroicons/react/24/solid';
import { OfflineStatusIcon } from './OfflineIndicator';

interface NavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  activeIcon: React.ReactNode;
  matchPaths?: string[];
}

/**
 * Navigation mobile agent terrain - Ecrans disponibles :
 * - Accueil : Liste des interventions assignees
 * - Profil : Informations agent et deconnexion
 *
 * NON inclus (fonctions desktop uniquement) :
 * - Regulation
 * - Export
 * - Utilisateurs
 * - Notifications
 */
const navItems: NavItem[] = [
  {
    path: '/agent',
    label: 'Missions',
    icon: <HomeIcon className="w-6 h-6" />,
    activeIcon: <HomeIconSolid className="w-6 h-6" />,
    matchPaths: ['/agent', '/agent/intervention'],
  },
  {
    path: '/agent/profil',
    label: 'Profil',
    icon: <UserCircleIcon className="w-6 h-6" />,
    activeIcon: <UserIconSolid className="w-6 h-6" />,
    matchPaths: ['/agent/profil'],
  },
];

export function MobileNavBar() {
  const location = useLocation();
  const navigate = useNavigate();

  const isActive = (item: NavItem) => {
    // Correspondance exacte
    if (location.pathname === item.path) return true;

    // Correspondance par prefixe, mais on verifie que c'est bien un sous-chemin
    if (item.matchPaths) {
      return item.matchPaths.some((p) => {
        if (location.pathname === p) return true;
        // S'assurer que c'est un vrai sous-chemin (avec /)
        return location.pathname.startsWith(p + '/');
      });
    }
    return false;
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 safe-area-inset-bottom">
      <div className="flex items-center justify-around h-16">
        {navItems.map((item) => {
          const active = isActive(item);
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={clsx(
                'flex flex-col items-center justify-center flex-1 h-full px-2 transition-colors',
                active ? 'text-primary-600' : 'text-gray-500'
              )}
            >
              {active ? item.activeIcon : item.icon}
              <span className="text-xs mt-1 font-medium">{item.label}</span>
            </button>
          );
        })}

        {/* Indicateur offline dans la navbar */}
        <div className="flex flex-col items-center justify-center px-2">
          <OfflineStatusIcon className="w-6 h-6" />
        </div>
      </div>
    </nav>
  );
}

export default MobileNavBar;
